/** Booking-integration Webhooks panel: last-received, install/uninstall, resend
 *  email, and enable/disable flags. Phase-2 rolling stats mount here later. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import type { IntegrationDetail } from '@/api/integrations';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { activityActions, sharedSecretActions, type TenantCtx } from '../tenantLinks';

import { WebhookUrls } from './WebhookUrls';
import { ChipListField } from './wizard/ChipListField';

const FLAGS: { key: string; label: string }[] = [
  { key: 'disable_webhooks', label: 'Disable all webhooks' },
  { key: 'disable_booking_webhooks', label: 'Disable booking webhooks' },
  { key: 'disable_order_webhooks', label: 'Disable order webhooks' },
  { key: 'disable_departure_webhooks', label: 'Disable departure webhooks' },
  { key: 'disable_product_webhooks', label: 'Disable product webhooks' },
];

const WEBHOOK_TYPES = ['booking', 'order', 'departure', 'product'] as const;

function fmtTs(ts: string | null | undefined): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

/** The shared secret that goes on the provider's side of a manual webhook setup.
 *
 *  Half of a manual setup is the endpoint URL (above) and half is this value —
 *  without both, deliveries either never arrive or arrive and are rejected.
 *  Shown masked for users without `console_restech_manage`, because the
 *  retrieve call only reveals secrets for them.
 */
export function WebhookSharedSecret({
  authRequired,
  token,
  canManage = false,
}: {
  authRequired: boolean;
  token: string;
  /** Only a manager is served the real value — a view-only user gets a mask,
   *  and a copied mask is worse than no copy at all. */
  canManage?: boolean;
}) {
  if (!authRequired && !token) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Shared secret
      </Typography>
      {token ? (
        <>
          <TextField
            size="small"
            fullWidth
            value={token}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <FieldActionsAdornment
                    actions={sharedSecretActions(token, canManage)}
                    label="Shared secret actions"
                  />
                ),
              },
            }}
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Set this same value as the webhook secret in the provider&apos;s dashboard. Change it in
            the Config tab (<code>webhook_auth_token</code>).
          </Typography>
        </>
      ) : (
        <Alert severity="warning">
          No shared secret is set. This system rejects inbound webhooks that do not carry one, so
          every delivery will be dropped until <code>webhook_auth_token</code> is set in the Config
          tab and the same value is configured on the provider&apos;s side.
        </Alert>
      )}
    </Box>
  );
}

interface Props {
  toId: number;
  toCode: string;
  integrationId: string;
  detail: IntegrationDetail;
  canManage: boolean;
  onChanged: () => void;
}

export function WebhookPanel({ toId, toCode, integrationId, detail, canManage, onChanged }: Props) {
  const snackbar = useSnackbar();
  const cfg = (detail.raw_config ?? {}) as Record<string, unknown>;
  const status = detail.webhook_status;
  const lastReceived = status?.webhook_last_received ?? detail.webhook_last_received ?? null;
  const mode = status?.webhook_setup_mode ?? detail.webhook_setup_mode;
  const canInstall = status?.can_install_webhook ?? detail.can_install_webhook;
  const restech = detail.restech ?? '';
  const ctx: TenantCtx = { id: toId, code: toCode };

  const [confirmUninstall, setConfirmUninstall] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const flagMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      integrationsApi.update(toId, integrationId, { config: patch, replace: false }),
    onSuccess: () => {
      snackbar.show('Webhook settings updated.', 'success');
      onChanged();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Update failed.', 'error'),
  });

  const installMutation = useMutation({
    mutationFn: () => integrationsApi.installWebhook(toId, integrationId),
    onSuccess: () => {
      snackbar.show('Webhook installed.', 'success');
      onChanged();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Install failed.', 'error'),
  });

  const uninstallMutation = useMutation({
    mutationFn: () => integrationsApi.uninstallWebhook(toId, integrationId),
    onSuccess: (r) => {
      snackbar.show(
        r.kept_foreign
          ? `Removed ${r.uninstalled} webhook(s); ${r.kept_foreign} belonging to other operators or environments left untouched.`
          : `Removed ${r.uninstalled} webhook(s).`,
        'success',
      );
      setConfirmUninstall(false);
      onChanged();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Uninstall failed.', 'error'),
  });

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Last received
        </Typography>
        <Table size="small">
          <TableBody>
            {WEBHOOK_TYPES.map((t) => {
              const at = lastReceived?.[t];
              return (
                <TableRow key={t}>
                  <TableCell sx={{ textTransform: 'capitalize', width: 140 }}>{t}</TableCell>
                  <TableCell sx={{ color: at ? 'text.primary' : 'text.disabled' }}>
                    {at ? (
                      // "What actually arrived then?" — the API-requests grid
                      // around that moment, narrowed to this system's traffic.
                      <ValueActions actions={activityActions(at, { integration: restech, t: ctx })}>
                        {fmtTs(at)}
                      </ValueActions>
                    ) : (
                      fmtTs(at)
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.disabled">
          {/* PHASE 2: rolling received/processed/skipped/errored counts (5m/1h/24h/7d) render here. */}
          Rolling delivery statistics are coming soon.
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Webhook setup
        </Typography>
        {mode === 'api' && (
          <Stack direction="row" spacing={2}>
            <Button variant="contained" disabled={!canManage || installMutation.isPending} onClick={() => installMutation.mutate()}>
              {installMutation.isPending ? 'Installing…' : 'Install / reinstall webhook'}
            </Button>
            <Button color="error" disabled={!canManage || uninstallMutation.isPending} onClick={() => setConfirmUninstall(true)}>
              Uninstall
            </Button>
          </Stack>
        )}
        {mode === 'email' && (
          <Button variant="outlined" disabled={!canManage} onClick={() => setEmailOpen(true)}>
            Resend setup email…
          </Button>
        )}
        {mode === 'manual' && (
          <Alert severity="info">
            Webhook setup for this system is manual — configure it in the provider&apos;s dashboard.
          </Alert>
        )}
        {mode === 'none' && <Alert severity="info">This integration does not receive webhooks.</Alert>}
        {!canInstall && mode === 'api' && (
          <Typography variant="caption" color="text.disabled" display="block">
            API installation is unavailable.
          </Typography>
        )}
      </Box>

      {mode !== 'none' && restech && (
        <>
          <Divider />
          <WebhookUrls toCode={toCode} restech={restech} slug={detail.slug} />
          <WebhookSharedSecret
            authRequired={detail.webhook_auth_required}
            token={typeof cfg.webhook_auth_token === 'string' ? cfg.webhook_auth_token : ''}
            canManage={canManage}
          />
        </>
      )}

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Enable / disable
        </Typography>
        {FLAGS.map((f) => (
          <FormControlLabel
            key={f.key}
            control={
              <Switch
                checked={Boolean(cfg[f.key])}
                disabled={!canManage || flagMutation.isPending}
                onChange={(e) => flagMutation.mutate({ [f.key]: e.target.checked })}
              />
            }
            label={f.label}
          />
        ))}
      </Box>

      <ConfirmDialog
        open={confirmUninstall}
        title="Uninstall webhook?"
        message="This removes this operator's own webhook registrations from the provider (webhooks of other operators sharing the API key are not touched). Bookings delivered through these webhooks will stop until reinstalled."
        confirmLabel="Uninstall"
        destructive
        onConfirm={() => uninstallMutation.mutate()}
        onCancel={() => setConfirmUninstall(false)}
      />

      {emailOpen && (
        <ResendEmailDialog
          toId={toId}
          integrationId={integrationId}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </Stack>
  );
}

const SEND_BY_HAND = 'Automatic e-mails to vendor support are off; send this by hand.';
const SANDBOX_NEVER =
  'A sandbox never e-mails vendor support. Do not send this by hand either: on a sandbox of a live operator the vendor would register these webhooks on its live account.';

/** The webhook-setup email (preview, edit, send) — also the easy flow's "Ask FareHarbor to
 *  send bookings" step, which passes its own title and hears about the send.
 *
 *  An automated e-mail to the vendor's support team: a sandbox never sends it, and production
 *  only while the platform switch is on. The preview says so beforehand (`send_enabled`); a send
 *  that is withheld answers 409 — with the text to send by hand when switched off — shown here
 *  as such, never as an error or as "sent". */
export function ResendEmailDialog({
  toId,
  integrationId,
  onClose,
  title = 'Resend webhook-setup email',
  onSent,
}: {
  toId: number;
  integrationId: string;
  onClose: () => void;
  title?: string;
  onSent?: () => void;
}) {
  const snackbar = useSnackbar();
  const [recipients, setRecipients] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [withheld, setWithheld] = useState<integrationsApi.WebhookEmailWithheld | null>(null);

  const previewQuery = useQuery({
    queryKey: ['integrations', 'webhookEmailPreview', toId, integrationId],
    queryFn: () => integrationsApi.webhookEmailPreview(toId, integrationId),
  });

  useEffect(() => {
    if (previewQuery.data) {
      setRecipients(previewQuery.data.recipients);
      setSubject(previewQuery.data.subject);
      setBody(previewQuery.data.body);
    }
  }, [previewQuery.data]);

  const sendMutation = useMutation({
    mutationFn: () =>
      integrationsApi.sendWebhookEmail(toId, integrationId, { recipients, subject, body }),
    onSuccess: () => {
      snackbar.show('Webhook-setup email sent.', 'success');
      onSent?.();
      onClose();
    },
    onError: (e: unknown) => {
      const w = integrationsApi.webhookEmailWithheld(e);
      if (w) setWithheld(w);
      else snackbar.show(e instanceof ConsoleApiError ? e.message : 'Send failed.', 'error');
    },
  });

  // Why it cannot be sent: the send's 409 once there is one, else the preview (absent = it sends).
  const preview = previewQuery.data;
  const reason: integrationsApi.WebhookEmailSendDisabledReason | null = withheld
    ? withheld.code === 'vendor_support_email_sandbox'
      ? 'sandbox'
      : 'disabled'
    : preview?.send_enabled === false
      ? (preview.send_disabled_reason ?? 'error')
      : null;
  const manual = withheld?.manual ?? null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {previewQuery.isError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void previewQuery.refetch()}>
                  Try again
                </Button>
              }
            >
              Could not load the webhook-setup e-mail
              {previewQuery.error instanceof ConsoleApiError ? `: ${previewQuery.error.message}` : '.'}
            </Alert>
          )}
          {reason === 'disabled' && <Alert severity="info">{SEND_BY_HAND}</Alert>}
          {reason === 'sandbox' && <Alert severity="info">{withheld?.detail || SANDBOX_NEVER}</Alert>}
          {reason === 'error' && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => void previewQuery.refetch()}>
                  Try again
                </Button>
              }
            >
              Could not tell whether this operator is a sandbox, so this e-mail cannot be sent. Try
              again later.
            </Alert>
          )}
          {manual ? (
            <ManualSendPanel manual={manual} />
          ) : (
            <>
              <ChipListField label="Recipients" values={recipients} onChange={setRecipients} placeholder="email@example.com" />
              <TextField label="Subject" size="small" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <TextField label="Body" size="small" multiline minRows={6} value={body} onChange={(e) => setBody(e.target.value)} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {manual ? (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={sendMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => sendMutation.mutate()}
              disabled={
                sendMutation.isPending ||
                recipients.length === 0 ||
                reason === 'sandbox' ||
                reason === 'error'
              }
            >
              {reason === 'disabled'
                ? sendMutation.isPending
                  ? 'Preparing…'
                  : 'Show text to send by hand'
                : sendMutation.isPending
                  ? 'Sending…'
                  : 'Send'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** The withheld e-mail to send by hand: each part read-only, with a copy button. */
function ManualSendPanel({ manual }: { manual: integrationsApi.WebhookEmailManual }) {
  const snackbar = useSnackbar();
  const parts: { label: string; copyLabel: string; done: string; value: string; multiline?: boolean }[] = [
    { label: 'To', copyLabel: 'Copy recipients', done: 'Recipients copied.', value: manual.recipients.join(', ') },
    { label: 'Reply-to', copyLabel: 'Copy reply-to', done: 'Reply-to copied.', value: manual.reply_to.join(', ') },
    { label: 'Subject', copyLabel: 'Copy subject', done: 'Subject copied.', value: manual.subject },
    { label: 'Body', copyLabel: 'Copy body', done: 'Body copied.', value: manual.body_text, multiline: true },
  ];

  async function copy(value: string, done: string) {
    try {
      await navigator.clipboard.writeText(value);
      snackbar.show(done, 'success');
    } catch {
      snackbar.show('Could not copy to clipboard.', 'error');
    }
  }

  return (
    <>
      {parts.map((p) => (
        <TextField
          key={p.label}
          label={p.label}
          size="small"
          fullWidth
          multiline={p.multiline}
          minRows={p.multiline ? 6 : undefined}
          value={p.value}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" sx={p.multiline ? { alignSelf: 'flex-start', mt: 1 } : undefined}>
                  <Tooltip title={p.copyLabel}>
                    <IconButton size="small" edge="end" aria-label={p.copyLabel} onClick={() => void copy(p.value, p.done)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
        />
      ))}
    </>
  );
}
